import IdbsAuth from '@idbs/idbs-auth';
import { Native } from './native';

/**
 * The Auth class provides a simple interface for managing authentication
 * with the IDBS Auth service. It acts as a singleton to simplify the API
 * and reduce the need for consumers to keep local copies of authentication
 * state.
 */
export class Auth {
  private static _tenantId: string | null = '';
  private static _idbsAuth: IdbsAuth | null;
  private static _userName: string | null = '';
  private static _nickname: string | null = '';
  private static _avatar: string | null = '';

  static {
    if (Native.getHostname() === 'localhost') {
      Auth._tenantId = import.meta.env.VITE_PAS_TENANT || 'pas-qa';
    } else {
      const pathSegments = Native.getPathname().split('/');
      Auth._tenantId = pathSegments[1] ?? null;
    }
  }

  /**
   * The tenant Id for the session
   */
  static get tenantId() {
    return Auth._tenantId;
  }

  /**
   * The IDBS user name for the session
   */
  static get userName() {
    return Auth._userName;
  }

  /**
   * The IDBS user nickname for the session
   */
  static get nickname() {
    return Auth._nickname;
  }

  /**
   * The user's avatar URL
   */
  static get avatar(): string | null {
    return Auth._avatar;
  }

  /**
   * Whether or not the user is authenticated
   */
  static get isLoggedIn(): boolean {
    return Auth._idbsAuth?.isAuthenticated() ?? false;
  }

  /**
   * The current access token for the session
   */
  static get accessToken(): string | null {
    return Auth._idbsAuth?.getTokens()?.accessToken ?? null;
  }

  /**
   * Starts a new IDBS session, returning a promise that resolves when the
   * session is successfully established.
   */
  static login() {
    return new Promise<void>((resolve, reject) => {
      Auth._userName = null;
      Auth._avatar = null;
      Auth._nickname = null;

      if (!Auth.tenantId) {
        reject(new Error('Tenant ID not available, login cannot proceed'));
        return;
      }

      Auth._idbsAuth = new IdbsAuth({
        tenantName: Auth.tenantId,
        realm: 'eln',
        scopes: 'eln',
        logoutRedirectUrl: `${location.origin}/${Auth.tenantId}`,
        endOfSessionPollPeriod: 60000,
        applicationNameCookiePrefix: 'ai-search',
      });

      Auth._idbsAuth.onPageLoad(
        (error, isAuthenticated) => {
          if (error || !isAuthenticated) {
            // a problem occurred or the user isn't currently logged in - trigger a login
            Auth._idbsAuth!.login(false, Auth._idbsAuth!.getLocation());
          }
        },
        (user) => {
          // Called once the login has succeeded and the user information is available
          const { picture, name, nickname, sub } = user;
          Auth._nickname = nickname;

          if (name || nickname) {
            Auth._userName = name || nickname;
          } else {
            // Backup - extract the username from the sub/subject claim
            const index = sub ? sub?.lastIndexOf('|') + 1 : 0;
            Auth._userName = sub.substring(index);
          }

          Auth._avatar = picture;

          // Indicate that the login has succeeded
          resolve();

          // Make sure that as long as the user is active, their session token will always be valid
          Auth._idbsAuth!.initRefresh();
        },
      );
    });
  }

  /**
   * Ends the IDBS session
   */
  static logout() {
    if (Auth._idbsAuth) {
      Auth._idbsAuth.logout();
      Auth._idbsAuth = null;
      Auth._userName = null;
      Auth._nickname = null;
      Auth._avatar = null;
    }
  }
}
